import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Notify } from '$src/utils/notify';

import NavigationTitle from '$src/components/NavigationTitle';
import SirenPreference from '$src/components/SirenPreference';

import './index.less';
import DevicesList from '$src/components/DevicesList';
import useStores from '$src/hooks/useStores';
import useLocationContext from '$src/hooks/useLocationContext';
import useFeature from '$src/hooks/useFeature';
import { DevicesIcon } from '@ring-icons';
import { CLICK, ENROLL_DEVICE, UNENROLL_DEVICE } from '$src/constants/actions';

const DevicesContainer = () => {
  const {
    sessionStore: { user },
    devicesStore: {
      fetchDevices,
      devicesInLocation,
      notEligibleDevices,
      eligibleIndoorDevices,
      eligibleOutdoorDevices,
      subscribeDevice,
      unsubscribeDevice,
      subscribedDevices,
    },
    modesStore: { fetchVSGModeStatus },
    statsStore: { sendStats },
    locationContextStore: { location, locationOwnerId },
  } = useStores();
  const [loading, setLoading] = useState(true);
  const locationId = useLocationContext();
  const [isMonitoringZonesEnabled] = useFeature('stark-feature-next-video-monitoring-zones');

  const handleSubscribe = async (device, subscribe) => {
    try {
      if (subscribe) {
        await subscribeDevice(device, locationOwnerId, locationId);
        sendStats(CLICK, device.doorbotId, ENROLL_DEVICE);
        Notify.success(`${device.name} successfully enrolled in Virtual Security Guard`);
      } else {
        await unsubscribeDevice(locationOwnerId, device.doorbotId, locationId);
        sendStats(CLICK, device.doorbotId, UNENROLL_DEVICE);
        Notify.success(`${device.name} successfully unenrolled from Virtual Security Guard`);
      }
      await fetchDevices(locationId, locationOwnerId);
    } catch (e) {
      Notify.error(`Error ${subscribe ? 'enrolling' : 'unenrolling'} the ${device.name} device`, e);
    } finally {
      fetchVSGModeStatus(locationOwnerId, locationId);
    }
  };

  useEffect(() => {
    async function getDevices() {
      await fetchDevices(locationId, locationOwnerId);
      setLoading(false);
    }
    if (locationId && locationOwnerId) getDevices();
  }, [fetchDevices, locationId, locationOwnerId]);

  return (
    <div data-testid="devices-page">
      <div className="page-devices-wrapper">
        <div className="page-header">
          <NavigationTitle
            title={isMonitoringZonesEnabled ? "Which cameras should be monitored?" : "Authorized Devices"}
            icon={() => <DevicesIcon className="navigation-title__icon" />}
            noMargin
          >
            {isMonitoringZonesEnabled
              ? "When a person is detected, our monitoring center only reviews videos from the cameras you select."
              : `Manage which devices are authorized for Guards to monitor at your ${location?.locationName ? `${location.locationName} ` : ''}location.`
            }
          </NavigationTitle>
        </div>
        <DevicesList
          devicesInLocation={devicesInLocation}
          notEligibleDevices={notEligibleDevices}
          eligibleOutdoorDevices={eligibleOutdoorDevices}
          eligibleIndoorDevices={eligibleIndoorDevices}
          subscribedDevices={subscribedDevices}
          sendStats={sendStats}
          user={user}
          warningOnToggleLastDevice
          loading={loading}
          handleSubscribe={handleSubscribe}
        />
        <SirenPreference user={user} />
      </div>
    </div>
  );
};

export default observer(DevicesContainer);
